import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';
import RestartAlt from '@mui/icons-material/RestartAlt';
import moment from 'moment';
import { orderDeliveryService } from '../../services/orderDelivery.service';
import GeneralCustomTable from '../../helpers/GeneralCustomTable';
import { GetOrderDeliveryList, SetOrderDeliveryList } from '../../store/orderDeliveryAtom';
import { amountToNumber, taskStatusClass } from './orderDeliveryStatus';
import LogicContext from '../../Context/LogicContext';

const formatDate = value => (value ? moment(value).format('DD-MM-YYYY HH:mm') : '-');

const columns = [
  {
    title: 'Order No',
    dataIndex: 'sales_order_ref_no',
    className: 'text-primary',
    sorter: (a, b) => (a?.sales_order_ref_no ?? 0) - (b?.sales_order_ref_no ?? 0),
  },
  {
    title: 'Recipient',
    dataIndex: 'order_recipient_name',
    className: 'text-primary font-bold',
    sorter: (a, b) => (a?.order_recipient_name ?? '').trim().localeCompare((b?.order_recipient_name ?? '').trim()),
    render: record => record?.trim(),
  },
  {
    title: 'Phone',
    dataIndex: 'order_recipient_phone_number',
    sorter: (a, b) => (a?.order_recipient_phone_number ?? '').localeCompare(b?.order_recipient_phone_number ?? ''),
  },
  {
    title: 'Amount',
    dataIndex: 'total_sales_order_amount',
    sorter: (a, b) => amountToNumber(a?.total_sales_order_amount) - amountToNumber(b?.total_sales_order_amount),
    render: record => <span className="font-bold">{record}</span>,
  },
  {
    title: 'Status',
    dataIndex: 'task_status_name',
    sorter: (a, b) => (a?.task_status_name ?? '').localeCompare(b?.task_status_name ?? ''),
    render: (record, row) => (
      <span
        className={`whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${
          taskStatusClass[row?.task_status_id] ?? 'bg-gray-100 text-gray-700'
        }`}
      >
        {record}
      </span>
    ),
  },
  {
    title: 'Created On',
    dataIndex: 'task_created_date',
    sorter: (a, b) => (a?.task_created_date ?? '').localeCompare(b?.task_created_date ?? ''),
    render: record => formatDate(record),
  },
  {
    title: 'Status Updated',
    dataIndex: 'task_status_update_date',
    sorter: (a, b) => (a?.task_status_update_date ?? '').localeCompare(b?.task_status_update_date ?? ''),
    render: record => formatDate(record),
  },
];

const today = () => moment().format('YYYY-MM-DD');

const searchFields = ['sales_order_ref_no', 'order_recipient_name', 'order_recipient_phone_number', 'total_sales_order_amount', 'task_status_name'];

const dateInputClass =
  'h-10 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500';

function OrderDeliveryMainPage() {
  const navigate = useNavigate();
  const logicContext = useContext(LogicContext);
  const { pathname } = useLocation();
  var url = pathname.split('/');
  const currModuleId = url[1];
  const currModuleName = url[2];
  const currUserMenu = url[3];

  const getOrderDelivery = GetOrderDeliveryList();
  const setOrderDelivery = SetOrderDeliveryList();

  // Rows the status and text filters apply to: the full list, or the date search result
  const [baseRows, setBaseRows] = useState([]);
  const [dateSearchActive, setDateSearchActive] = useState(false);
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(today());
  const [statusId, setStatusId] = useState('');
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    document.title = 'Order Delivery';
    let isMounted = true;
    if (getOrderDelivery.task_list?.length) {
      setBaseRows(getOrderDelivery.task_list);
    } else {
      loadOrderDelivery(() => isMounted);
    }

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOrderDelivery = (isMounted = () => true) => {
    orderDeliveryService.getOrderDeliveryList().then(res => {
      if (res?.data && isMounted()) {
        setOrderDelivery(res.data);
        setBaseRows(res.data.task_list ?? []);
        setDateSearchActive(false);
      }
    });
  };

  const searchByDate = () => {
    if (!fromDate || !toDate) {
      logicContext.showToast('Select both From and To dates', 'error');
      return;
    }
    const body = { from_date: fromDate, to_date: toDate };
    if (statusId) {
      body.task_status_id = Number(statusId);
    }
    orderDeliveryService.filterOrderDelivery(body).then(res => {
      const rows = res?.data?.task_list ?? (Array.isArray(res?.data) ? res.data : []);
      setBaseRows(rows);
      setDateSearchActive(true);
      if (!rows.length) {
        logicContext.showToast('No available record for your search query!', '');
      }
    });
  };

  const resetDateSearch = () => {
    setBaseRows(getOrderDelivery.task_list ?? []);
    setDateSearchActive(false);
    setFromDate(today());
    setToDate(today());
  };

  const statusOptions = useMemo(() => {
    const counts = new Map();
    baseRows.forEach(item => {
      if (!item?.task_status_id) return;
      const current = counts.get(item.task_status_id) ?? { name: item.task_status_name, count: 0 };
      counts.set(item.task_status_id, { ...current, count: current.count + 1 });
    });
    return [...counts.entries()].sort((a, b) => a[0] - b[0]);
  }, [baseRows]);

  const tableData = useMemo(() => {
    const text = searchText.trim().toLowerCase();
    return baseRows.filter(item => {
      if (statusId && String(item?.task_status_id) !== statusId) return false;
      if (!text) return true;
      return searchFields.some(field =>
        String(item?.[field] ?? '')
          .toLowerCase()
          .includes(text),
      );
    });
  }, [baseRows, statusId, searchText]);

  const paginationOptions = {
    pageSize: 10,
    showSizeChanger: true,
    total: tableData.length,
  };

  function handleRowClick(row) {
    navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/details/${row?.sales_order_ref_no}`);
  }

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <Tooltip title="Reload">
            <RestartAlt
              className="mb-1 cursor-pointer text-blue-600 transition-colors hover:text-blue-800"
              onClick={() => loadOrderDelivery()}
              color="primary"
              fontSize="large"
            />
          </Tooltip>

          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700">From Date</label>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className={dateInputClass} />
          </div>
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700">To Date</label>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className={dateInputClass} />
          </div>

          <button
            type="button"
            onClick={searchByDate}
            className="h-10 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
          >
            Search
          </button>
          {dateSearchActive && (
            <button
              type="button"
              onClick={resetDateSearch}
              className="h-10 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
            >
              Reset
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <select
            value={statusId}
            onChange={e => setStatusId(e.target.value)}
            className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All statuses</option>
            {statusOptions.map(([value, { name, count }]) => (
              <option key={value} value={String(value)}>
                {name} ({count})
              </option>
            ))}
          </select>

          <div className="relative">
            <input
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="Search in table..."
              className="h-10 w-64 rounded-md border border-gray-300 bg-white pl-3 pr-8 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            />
            {searchText && (
              <button
                type="button"
                onClick={() => setSearchText('')}
                className="absolute inset-y-0 right-2 flex items-center text-gray-400 hover:text-gray-600"
                title="Clear"
              >
                ×
              </button>
            )}
          </div>
        </div>
      </div>

      <p className="mt-3 text-xs text-gray-500">
        Showing {tableData.length} of {baseRows.length} orders
        {dateSearchActive && ` · ${moment(fromDate).format('DD-MM-YYYY')} to ${moment(toDate).format('DD-MM-YYYY')}`}
      </p>

      <section className="mt-2">
        <GeneralCustomTable
          tableHeadList={columns}
          tableDataList={tableData}
          allowCheckBox={false}
          paginationOptions={paginationOptions}
          rowKey={record => record?.sales_order_ref_no}
          handleRowClick={handleRowClick}
        />
      </section>
    </>
  );
}

export default OrderDeliveryMainPage;
